import { Component, EventEmitter, Input, OnInit, Output, SimpleChange } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DID_CLASS, DID_STATUS, DID_TYPE, DID_TEMPORARY } from 'voxloud-types/dist/models';

import { DidsService } from 'src/app/domains/dids/services';
import { FilterModel, LockedFilterModel, FiltersEmitModel } from 'src/app/domains/dids/models';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';


@Component({
  selector: 'gdp-company-dids-filters',
  templateUrl: './company-dids-filters.component.html',
  styleUrls: ['./company-dids-filters.component.scss']
})
export class CompanyDidsFiltersComponent implements OnInit {
  public usedFilters: FilterModel[] = [
  ];
  public availableFilters: FilterModel[] = [
    {
      name: 'company_id',
      type: 'input',
      isLocked: false
    },
    {
      name: 'number',
      type: 'input',
      isLocked: false
    },
    {
      name: 'provider',
      type: 'dropdown-provider',
      isLocked: false
    },
    {
      name: 'country_code',
      type: 'country_selector',
      isLocked: false
    },
    {
      name: 'class',
      type: 'class',
      data: Object.keys(DID_CLASS),
      isLocked: false
    },
    {
      name: 'area_code',
      type: 'prefix',
      isLocked: false
    },
    {
      name: 'status',
      type: 'dropdown',
      data: Object.keys(DID_STATUS),
      isLocked: false
    },
    {
      name: 'type',
      type: 'type',
      data: Object.keys(DID_TYPE),
      isLocked: false
    },
    {
      name: 'creation_date_from',
      type: 'datepicker',
      isLocked: false
    },
    {
      name: 'creation_date_to',
      type: 'datepicker',
      isLocked: false
    },
    {
      name: 'is_from_pool',
      type: 'dropdown',
      data: Object.keys(DID_TEMPORARY),
      isLocked: false
    }
  ].sort(this.sortCallback);
  @Input() lockedFilters: LockedFilterModel[] = [];
  @Input() quickFilters: FiltersEmitModel[];
  @Input() set importedFilters(value: FiltersEmitModel) {
    if (value) this.parseExternalFilters(value);
  }
  @Output() onFilter: EventEmitter<FiltersEmitModel> = new EventEmitter();

  public isPrefixLoading = false;
  public form: UntypedFormGroup;

  public currentLanguage: string;

  constructor(public calendar: NgbCalendar, private didService: DidsService, private translate: TranslateService) { }

  ngOnInit() {
    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((l: LangChangeEvent) => {
      this.currentLanguage = l.lang;
    });
    this.didService.getProviders().subscribe(p => {
      let filter = this.availableFilters.find(a => a.name === 'provider');
      filter.data = p.map(e => e.name);
    });
    this.form = new UntypedFormGroup({});
    this.setLockedFilter();
  }

  get canFilter() {
    return Object.keys(this.form.value).length > 0;
  }

  public addFilter(index: number, defaultValue?: any): void {
    const filter = this.availableFilters.splice(index, 1)[0];
    let formControl: UntypedFormControl;
    this.usedFilters.push(filter);
    if (Array.isArray(filter.data)) {
      formControl = new UntypedFormControl(filter.data[0] || '');
      this.form.addControl(filter.name, formControl);
    } else {
      formControl = new UntypedFormControl(filter.data || '');
      this.form.addControl(filter.name, formControl);
    }
    if (defaultValue) formControl.setValue(defaultValue);
    this.checkIfAreaCode(filter);
  }

  public removeFilter(index: number): void {
    const filter = this.usedFilters.splice(index, 1)[0];
    this.form.removeControl(filter.name);
    this.availableFilters.push(filter);
    this.availableFilters.sort(this.sortCallback);
  }

  public swapFilters(used: number, available: number): void {
    this.form.removeControl(this.usedFilters[used].name);
    this.form.addControl(this.availableFilters[available].name,
      Array.isArray(this.availableFilters[available].data) ?
        new UntypedFormControl(this.availableFilters[available].data[0] || null) :
        new UntypedFormControl('')
    );
    const tmp = this.availableFilters.splice(available, 1)[0];
    this.availableFilters.push(this.usedFilters[used]);
    this.usedFilters[used] = tmp;
    this.checkIfAreaCode(tmp);
    this.availableFilters.sort(this.sortCallback);
  }

  private sortCallback(a: FilterModel, b: FilterModel): number {
    const upperA = a.name.toUpperCase();
    const upperB = b.name.toUpperCase();
    if (upperA < upperB) return 1;
    if (upperA > upperB) return -1;
    return 0;
  }

  private setLockedFilter() {
    this.lockedFilters.forEach(
      l => {
        let fc = this.form.get(l.name);
        if (!fc) {
          this.addFilter(
            this.availableFilters.findIndex(f => f.name === l.name), l.value
          );
        } else {
          fc.setValue(l.value);
        }
        this.usedFilters.find(u => u.name === l.name).isLocked = true;
      }
    )
  }

  public emitFilters() {
    let values = this.form.value;
    if (this.checkAtLeastOneFilled(values)) {
      let filters: FiltersEmitModel = {
        ...values
      };
      if (values.area_code) filters.area_code = values.area_code.code;
      if (values.creation_date_from) filters.creation_date_from = this.stringifyDate(values.creation_date_from);
      if (values.creation_date_to) filters.creation_date_to = this.stringifyDate(values.creation_date_to);
      if (values.is_from_pool) {
        filters.is_from_pool = values.is_from_pool === DID_TEMPORARY.TEMPORARY;
      }
      this.onFilter.emit(filters);
    } else {
      this.onFilter.emit({});
    }
  }

  public resetFilters(shouldEmit: boolean = true) {
    for (let i = 0; i < this.usedFilters.length; i++) {
      if (this.lockedFilters.find(locked => this.usedFilters[i].name === locked.name)) continue;
      let filter = this.usedFilters.splice(i, 1)[0];
      filter.isLocked = false;
      this.form.removeControl(filter.name);
      this.availableFilters.push(filter);
      i--;
    }
    this.availableFilters.sort(this.sortCallback);
    if (shouldEmit)
      this.emitFilters();
  }

  private checkAtLeastOneFilled(values: any): boolean {
    for (let k of Object.keys(values)) {
      if (!values[k]) delete values[k];
    }
    return Object.keys(values).length > 0;
  }

  private stringifyDate(date: NgbDate) {
    return `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day.toString().padStart(2, '0')}`;
  }

  private checkIfAreaCode(filter: FilterModel) {
    if (filter.name === 'area_code' && !this.form.get('country_code')) {
      let countCodeIndex = this.availableFilters.findIndex(a => a.name === 'country_code');
      let countryCode = this.availableFilters.splice(countCodeIndex, 1)[0];
      this.usedFilters.push(countryCode);
      this.form.addControl('country_code', new UntypedFormControl('IT')); // use Italy by default
    }
  }

  public parseExternalFilters(filters: FiltersEmitModel) {
    this.resetFilters(false);
    for (let key of Object.keys(filters)) {
      this.addFilter(this.availableFilters.findIndex(f => f.name === key), filters[key]);
    }
    this.emitFilters();
  }

  public isFilterSelectorEnabled(used: FilterModel) {
    return this.availableFilters.length < 1 ||
      used.isLocked ||
      used.name === 'country_code' &&
      this.form.get('area_code');
  }
}
